import React, { useEffect, useMemo } from "react";
import {
  Avatar,
  Box,
  FormHelperText,
  Paper,
  Typography,
  useTheme,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import Groups2OutlinedIcon from "@mui/icons-material/Groups2Outlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import RepeatOutlinedIcon from "@mui/icons-material/RepeatOutlined";
import {
  buildEventRecurrencePayload,
  summarizeEventRecurrence,
} from "../../domain/events/eventRecurrence";
import { alpha } from "@mui/material/styles";
import ReviewHorizontalCarousel from "./ReviewHorizontalCarousel";
import ComposerStepCard from "./ComposerStepCard";
import NoticeBackgroundPicker from "./NoticeBackgroundPicker";
import { useAuth } from "../../context/AuthContext";
import { useAccessibleCircles } from "../../hooks/useAccessibleCircles";
import NoticeCard from "../NoticeCard";
import NoticeCardPreview from "./NoticeCardPreview";
import ImageNoticeCardPreview from "./ImageNoticeCardPreview";
import { NoticeComposerAttachmentsGallery } from "./NoticeComposerAttachmentsGallery";
import { isDriveVideoFile } from "../../utils/driveFolder";
import { resolveImageNoticeHeroMedia } from "../../utils/imageNoticeHero";
import { getAllBackgroundOptions } from "../../config/assets";
import { flowHasPublishConfiguredBeforeReview } from "../../config/noticeFlows";
import { formatEventDisplayDate } from "../../utils/eventDateDisplay";
import {
  getAudienceCircleChipBackground,
  getNoticeFeedMetaChipSx,
  getNoticeFeedTypeChipBackground,
  resolveNoticeFeedTypeChip,
  type NoticeStoredType,
} from "../../utils/noticeFeedChips";
import PublishScheduleOptions from "../scheduling/PublishScheduleOptions";
import SaveToDriveFolderOptions from "../scheduling/SaveToDriveFolderOptions";
import {
  INTERNAL_TEAM_ONLY_LABEL,
  noticeTypeSupportsScheduledPublish,
} from "../../utils/noticeScheduling";
import {
  listNoticeAudienceOptions,
  noticeAudiencePickPatch,
  reviewStaffOnlyClearPatch,
  settleNoticeAudience,
  writeNoticeAudiencePatch,
} from "../../domain/notices/noticeAudience";

interface StepProps {
  formData: {
    title: string;
    body: string;
    type: string;
    audience: string;
    attachmentUrl: string;
    uploadedFiles?: Array<{
      fileUrl: string;
      fileName: string;
      contentType: string;
      size: number;
    }>;
    eventStartDate?: Date | null;
    eventStartTime?: string;
    eventEndDate?: Date | null;
    eventEndTime?: string;
    eventLocation?: string;
    imageUrl?: string;
    eventBackgroundImage?: string;
    targetSubcircle?: string;
    isUrgent?: boolean;
    publishMode?: "now" | "schedule";
    publishAt?: string;
    staffOnly?: boolean;
    driveFolderKey?: string;
    eventRecurrenceFrequency?: string;
    eventRecurrenceEndMode?: "count" | "until";
    eventRecurrenceCount?: number;
    eventRecurrenceUntil?: Date | null;
  };
  errors: {
    title?: string;
    body?: string;
    type?: string;
    audience?: string;
    attachmentUrl?: string;
    eventStartDate?: string;
    eventStartTime?: string;
    eventEndDate?: string;
    eventEndTime?: string;
    eventLocation?: string;
    targetSubcircle?: string;
    publishAt?: string;
  };
  handleChange: (
    e:
      | React.ChangeEvent<
          HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
        >
      | { target: { name: string; value: any } }
  ) => void;
  /** Batched form updates; clears related validation errors. */
  applyFormPatch?: (patch: Record<string, unknown>) => void;
  /** Wizard flow id (`image_notice` stores formData.type as `image`). */
  noticeFlowId?: string;
}

export const ReviewStep: React.FC<StepProps> = ({
  formData,
  errors,
  handleChange,
  applyFormPatch,
  noticeFlowId,
}) => {
  const theme = useTheme();
  const { user } = useAuth();
  const { subcircles, canAccessMainCircle, isLoading: circlesLoading } =
    useAccessibleCircles();

  const audienceCatalog = useMemo(
    () => ({
      organizationName: user?.schoolName,
      organizationLogo: user?.schoolLogo || undefined,
      canChooseEveryone: canAccessMainCircle,
      circles: subcircles,
    }),
    [canAccessMainCircle, subcircles, user?.schoolLogo, user?.schoolName],
  );

  useEffect(() => {
    const settled = settleNoticeAudience(
      "review",
      audienceCatalog,
      {
        audience: formData.audience,
        targetSubcircle: formData.targetSubcircle,
        staffOnly: formData.staffOnly,
      },
      { isDirect: formData.type === "direct" },
    );
    if (settled) {
      writeNoticeAudiencePatch(settled, applyFormPatch, handleChange);
    }
  }, [
    applyFormPatch,
    audienceCatalog,
    formData.audience,
    formData.staffOnly,
    formData.targetSubcircle,
    formData.type,
    handleChange,
  ]);

  const isDirectNotice = formData.type === "direct";
  const isStaffOnly = formData.staffOnly === true;
  const hasPublishConfiguredBeforeReview = noticeFlowId
    ? flowHasPublishConfiguredBeforeReview(noticeFlowId)
    : false;
  const showPublishScheduleOptions =
    !isDirectNotice &&
    !hasPublishConfiguredBeforeReview &&
    noticeTypeSupportsScheduledPublish(formData.type) &&
    !isStaffOnly;
  const hasAttachments =
    Boolean(formData.attachmentUrl?.trim()) ||
    Boolean(formData.uploadedFiles && formData.uploadedFiles.length > 0);
  const showSaveToDriveFolder =
    !isDirectNotice && hasAttachments && formData.type !== "poll";
  const isEventNotice =
    formData.type === "event" || formData.type === "custom_event";

  useEffect(() => {
    const clearStaffOnly = reviewStaffOnlyClearPatch(
      isEventNotice,
      formData.staffOnly,
    );
    if (clearStaffOnly) {
      writeNoticeAudiencePatch(clearStaffOnly, applyFormPatch, handleChange);
    }
  }, [
    applyFormPatch,
    formData.staffOnly,
    handleChange,
    isEventNotice,
  ]);
  const isImageNotice =
    formData.type === "image" || formData.type === "image_notice";
  const isNativeImageNoticePreview = noticeFlowId === "image_notice";
  const isUrgent =
    !!formData.isUrgent &&
    (formData.type === "info" || formData.type === "direct");
  const showBackgroundPicker = !isImageNotice;

  const previewAttachments = useMemo(
    () =>
      (formData.uploadedFiles || []).map((file) => ({
        name: file.fileName,
        url: file.fileUrl,
        type: file.contentType,
      })),
    [formData.uploadedFiles]
  );

  const previewCardType: NoticeStoredType =
    formData.type === "image_notice"
      ? "image_notice"
      : formData.type === "image"
        ? "image"
        : formData.type === "direct"
          ? "direct"
          : isEventNotice
            ? "event"
            : "info";

  const resolvedPreviewChipType = useMemo(
    () => resolveNoticeFeedTypeChip(previewCardType, previewAttachments),
    [previewCardType, previewAttachments]
  );

  const noticeTypeLabel = isDirectNotice
    ? isUrgent
      ? "Urgent Direct Notice"
      : "Direct Notice"
    : isUrgent
    ? "Urgent Notice"
    : isEventNotice
    ? "Event Notice"
    : resolvedPreviewChipType === "video"
    ? "Video Notice"
    : isImageNotice
    ? "Image Notice"
    : "Info Notice";

  const audienceOptions = useMemo(
    () =>
      listNoticeAudienceOptions({
        catalog: audienceCatalog,
        wire: {
          audience: formData.audience,
          targetSubcircle: formData.targetSubcircle,
          staffOnly: formData.staffOnly,
        },
        includeInternalTeam: isEventNotice,
      }),
    [
      audienceCatalog,
      formData.audience,
      formData.staffOnly,
      formData.targetSubcircle,
      isEventNotice,
    ],
  );

  const selectedAudienceOption = useMemo(
    () => audienceOptions.find((option) => option.selected) ?? null,
    [audienceOptions],
  );

  const backgroundOptions = useMemo(() => {
    if (!showBackgroundPicker) return [];
    return getAllBackgroundOptions(user?.schoolType);
  }, [showBackgroundPicker, user?.schoolType]);

  const selectedBackground = formData.eventBackgroundImage || "";

  /** NoticeCard renders audience + notice-type chips; skip duplicate strip above preview. */
  const livePreviewShowsChipsOutsideCardOnly =
    !isNativeImageNoticePreview &&
    Boolean(selectedBackground || isEventNotice);

  const imageNoticeHeroMedia = useMemo(() => {
    if (!isNativeImageNoticePreview) return null;
    return resolveImageNoticeHeroMedia(
      formData.uploadedFiles,
      formData.attachmentUrl,
      formData.imageUrl
    );
  }, [
    isNativeImageNoticePreview,
    formData.uploadedFiles,
    formData.attachmentUrl,
    formData.imageUrl,
  ]);

  const composerGalleryFiles = useMemo(
    () =>
      (formData.uploadedFiles || []).filter(
        (f) =>
          f.contentType?.startsWith("image/") ||
          isDriveVideoFile(f.contentType, f.fileName)
      ),
    [formData.uploadedFiles]
  );

  /** NoticeCard detail preview already renders image/video beside the body. */
  const previewUsesNoticeCardDetail =
    !isNativeImageNoticePreview &&
    !(selectedBackground || isEventNotice);

  const showComposerAttachmentsGallery =
    composerGalleryFiles.length > 0 &&
    !previewUsesNoticeCardDetail &&
    (!isNativeImageNoticePreview || composerGalleryFiles.length > 1);

  /** Native feed header: subcircle name + org line, else main school name. */
  const postingSubcircleMeta = useMemo(() => {
    if (!formData.targetSubcircle) return null;
    return (
      subcircles.find((s) => s.circleId === formData.targetSubcircle) ?? null
    );
  }, [formData.targetSubcircle, subcircles]);

  const audienceLabel = formData.staffOnly
    ? INTERNAL_TEAM_ONLY_LABEL
    : formData.audience === "circle"
      ? user?.schoolName || "Entire Circle"
      : postingSubcircleMeta?.name || selectedAudienceOption?.label || "";

  const imagePreviewHeaderSchoolName = useMemo(() => {
    if (postingSubcircleMeta?.name) return postingSubcircleMeta.name;
    return user?.schoolName?.trim() || "School";
  }, [postingSubcircleMeta, user?.schoolName]);

  const imagePreviewOrganizationName = useMemo(() => {
    if (postingSubcircleMeta && user?.schoolName?.trim()) {
      return user.schoolName.trim();
    }
    return null;
  }, [postingSubcircleMeta, user?.schoolName]);

  const recurrenceSummary = useMemo(() => {
    if (
      !formData.eventRecurrenceFrequency ||
      formData.eventRecurrenceFrequency === "none"
    ) {
      return "";
    }
    const startDate =
      formData.eventStartDate instanceof Date
        ? formData.eventStartDate
        : formData.eventStartDate
          ? new Date(formData.eventStartDate)
          : null;
    return summarizeEventRecurrence(
      buildEventRecurrencePayload(
        formData.eventRecurrenceFrequency,
        formData.eventRecurrenceUntil || null,
      ),
      startDate,
    );
  }, [formData]);

  const summaryChips = [
    {
      key: "circle",
      kind: "audience" as const,
      label: audienceLabel,
      icon: <Groups2OutlinedIcon sx={{ fontSize: 15 }} />,
    },
    {
      key: "noticeType",
      kind: "noticeType" as const,
      label: noticeTypeLabel,
      icon: <DescriptionOutlinedIcon sx={{ fontSize: 15 }} />,
    },
    ...(formData.eventStartDate
      ? [
          {
            key: "startDate",
            kind: "meta" as const,
            label: formatEventDisplayDate(formData.eventStartDate),
            icon: <EventOutlinedIcon sx={{ fontSize: 15 }} />,
          },
        ]
      : []),
    ...(formData.eventLocation
      ? [
          {
            key: "location",
            kind: "meta" as const,
            label: formData.eventLocation,
            icon: <PlaceOutlinedIcon sx={{ fontSize: 15 }} />,
          },
        ]
      : []),
    ...(recurrenceSummary
      ? [
          {
            key: "recurrence",
            kind: "meta" as const,
            label: recurrenceSummary,
            icon: <RepeatOutlinedIcon sx={{ fontSize: 15 }} />,
          },
        ]
      : []),
  ];

  const handleAudienceSelect = (value: string) => {
    writeNoticeAudiencePatch(
      noticeAudiencePickPatch(value, { includeStaffOnly: true }),
      applyFormPatch,
      handleChange,
    );
  };

  const handleBackgroundSelect = (value: string) => {
    handleChange({
      target: { name: "eventBackgroundImage", value },
    });
  };

  /** Matches TitleStep / ContentStep `Message` section headings (`StepComponents`) */
  const sectionHeadingSx = {
    fontWeight: 400,
    color: "primary.main",
    fontSize: { xs: "1.125rem", sm: "1.25rem" },
  };

  return (
    <Box
      sx={{
        width: "100%",
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          md: "minmax(0, 1fr) 308px",
        },
        gap: 0,
        alignItems: { xs: "stretch", md: "start" },
        borderRadius: 1,
        bgcolor: "background.paper",
        overflowX: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
          borderRight: { md: `1px solid ${theme.palette.divider}` },
          borderBottom: {
            xs: `1px solid ${theme.palette.divider}`,
            md: "none",
          },
        }}
      >
        {showPublishScheduleOptions && (
          <Box
            sx={{
              px: { xs: 1, md: 1.25 },
              pt: { xs: 1, md: 1 },
              pb: { xs: 1.15, md: 1.15 },
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}
          >
            <PublishScheduleOptions
              publishMode={formData.publishMode || "now"}
              publishAt={formData.publishAt || ""}
              onPublishModeChange={(mode) =>
                handleChange({
                  target: { name: "publishMode", value: mode },
                })
              }
              onPublishAtChange={(value) =>
                handleChange({
                  target: { name: "publishAt", value },
                })
              }
            />
            {errors.publishAt && (
              <FormHelperText error sx={{ mt: 0.5 }}>
                {errors.publishAt}
              </FormHelperText>
            )}
          </Box>
        )}

        {!isDirectNotice && (
          <Box
            sx={{
              px: { xs: 1, md: 1.25 },
              pt: { xs: 1, md: 1 },
              pb: { xs: 1.15, md: 1.15 },
              borderBottom: showBackgroundPicker
                ? `1px solid ${theme.palette.divider}`
                : "none",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "stretch",
                gap: 2,
              }}
            >
              <ComposerStepCard title="Who should receive this?">
                <ReviewHorizontalCarousel
                  contentKey={audienceOptions.map((o) => o.id).join("|")}
                >
                  <Box
                    sx={{
                      display: "flex",
                      gap: { xs: 1.25, md: 1.5 },
                      minWidth: "max-content",
                      px: 0.2,
                      py: 0.25,
                    }}
                  >
                    {audienceOptions.map((option) => (
                      <Box
                        key={option.id}
                        onClick={() => handleAudienceSelect(option.id)}
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          textAlign: "center",
                          minWidth: { xs: 64, md: 70 },
                          maxWidth: { xs: 80, md: 88 },
                          cursor: "pointer",
                          color: "text.primary",
                        }}
                      >
                        <Box
                          sx={{
                            position: "relative",
                            borderRadius: "50%",
                            p: option.selected ? "1.5px" : "0px",
                            border: option.selected
                              ? "1.5px solid"
                              : "1.5px solid transparent",
                            borderColor: option.selected
                              ? "primary.main"
                              : "transparent",
                            transition: "all 0.18s ease",
                          }}
                        >
                          <Avatar
                            src={option.avatarSrc}
                            alt={option.label}
                            sx={{
                              width: { xs: 44, md: 48 },
                              height: { xs: 44, md: 48 },
                              bgcolor: alpha(theme.palette.primary.main, 0.12),
                              color: "primary.main",
                              fontWeight: 600,
                              fontSize: { xs: "0.7rem", md: "0.75rem" },
                              boxShadow: "none",
                            }}
                          >
                            {option.avatarText}
                          </Avatar>
                          {option.selected && (
                            <Box
                              sx={{
                                position: "absolute",
                                top: -1,
                                right: -1,
                                width: 16,
                                height: 16,
                                borderRadius: "50%",
                                bgcolor: "primary.main",
                                color: "common.white",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                border: "1.5px solid",
                                borderColor: "common.white",
                              }}
                            >
                              <CheckIcon sx={{ fontSize: 10 }} />
                            </Box>
                          )}
                        </Box>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 400,
                            mt: 0.45,
                            lineHeight: 1.25,
                          }}
                        >
                          {option.label}
                        </Typography>
                        {option.subtitle ? (
                          <Typography
                            variant="caption"
                            sx={{
                              color: "text.secondary",
                              mt: 0.1,
                              lineHeight: 1.25,
                            }}
                          >
                            {option.subtitle}
                          </Typography>
                        ) : null}
                      </Box>
                    ))}
                  </Box>
                </ReviewHorizontalCarousel>

                {(errors.audience || errors.targetSubcircle) &&
                  !selectedAudienceOption && (
                  <FormHelperText error sx={{ mt: 1 }}>
                    {errors.audience || errors.targetSubcircle}
                  </FormHelperText>
                )}

                {!circlesLoading && audienceOptions.length === 0 && (
                  <Typography
                    variant="body2"
                    sx={{ color: "text.secondary", mt: 1 }}
                  >
                    No circles are available for this notice yet.
                  </Typography>
                )}
              </ComposerStepCard>

              {showSaveToDriveFolder && (
                <ComposerStepCard
                  title="Drive folder"
                  subtitle="Where attachments are filed in Drive for this circle."
                  optional
                >
                  <SaveToDriveFolderOptions
                    layout="card"
                    audience={formData.audience}
                    targetSubcircle={formData.targetSubcircle}
                    staffOnly={formData.staffOnly}
                    audienceLabel={audienceLabel}
                    audienceAvatarSrc={selectedAudienceOption?.avatarSrc}
                    audienceAvatarText={selectedAudienceOption?.avatarText || ""}
                    driveFolderKey={formData.driveFolderKey || ""}
                    publishMode={formData.publishMode || "now"}
                    onDriveFolderKeyChange={(value) =>
                      handleChange({
                        target: { name: "driveFolderKey", value },
                      })
                    }
                  />
                </ComposerStepCard>
              )}
            </Box>
          </Box>
        )}

        {showBackgroundPicker && (
          <Box
            sx={{
              px: { xs: 1, md: 1.25 },
              pt: { xs: 1, md: 1 },
              pb: { xs: 1, md: 1.1 },
            }}
          >
            <NoticeBackgroundPicker options={backgroundOptions} value={selectedBackground} onChange={handleBackgroundSelect} noticeText={`${formData.title || ""} ${formData.body || ""}`} />
          </Box>
        )}
      </Box>

      <Box
        sx={{
          position: "sticky",
          top: { md: 12 },
          alignSelf: "start",
          minWidth: 0,
          px: { xs: 1, md: 1.25 },
          pt: { xs: 1, md: 1 },
          pb: { xs: 1, md: 1 },
        }}
      >
        <Typography
          variant="h6"
          sx={{ ...sectionHeadingSx, mb: 1 }}
        >
          Live preview
        </Typography>

        {livePreviewShowsChipsOutsideCardOnly ? (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 0.75,
              mb: 1.25,
            }}
          >
            {summaryChips.map((chip) => {
              const compact = {
                display: "inline-flex" as const,
                alignItems: "center",
                gap: 0.55,
                px: 0.75,
                py: 0.42,
                borderRadius: 999,
                "& .MuiSvgIcon-root": { fontSize: 15, opacity: 0.92 },
              };
              let sxExtra: Record<string, unknown> =
                chip.kind === "audience"
                  ? {
                      ...compact,
                      bgcolor: getAudienceCircleChipBackground(
                        theme,
                        audienceLabel
                      ),
                      color: theme.palette.common.white,
                    }
                  : chip.kind === "noticeType"
                  ? {
                      ...compact,
                      bgcolor: isUrgent
                        ? theme.palette.template.urgentPill
                        : getNoticeFeedTypeChipBackground(
                            theme,
                            resolvedPreviewChipType
                          ),
                      color: theme.palette.common.white,
                    }
                  : {
                      ...compact,
                      ...getNoticeFeedMetaChipSx(theme),
                    };
              return (
                <Box key={chip.key} sx={sxExtra}>
                  {chip.icon}
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 700, fontSize: "0.68rem", lineHeight: 1.25 }}
                  >
                    {chip.label}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        ) : null}

          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "center",
              overflow: "hidden",
              width: "100%",
            }}
          >
          {isNativeImageNoticePreview ? (
            <Box
              sx={{
                width: "100%",
                maxWidth: { xs: 300, md: 300 },
                mx: "auto",
                transform: { xs: "scale(0.82)", md: "scale(0.73)" },
                transformOrigin: "top center",
              }}
            >
              <ImageNoticeCardPreview
                title={formData.title}
                body={formData.body}
                imageUrl={imageNoticeHeroMedia?.url}
                heroIsVideo={imageNoticeHeroMedia?.isVideo}
                createdAt={new Date()}
                schoolName={imagePreviewHeaderSchoolName}
                schoolLogo={user?.schoolLogo || undefined}
                organizationName={imagePreviewOrganizationName}
                isUrgent={isUrgent}
              />
            </Box>
          ) : selectedBackground || isEventNotice ? (
            <Box
              sx={{
                width: "100%",
                maxWidth: { xs: 300, md: 300 },
                transform: { xs: "scale(0.8)", md: "scale(0.72)" },
                transformOrigin: "top center",
              }}
            >
              <NoticeCardPreview
                title={formData.title}
                body={formData.body}
                eventStartDate={formData.eventStartDate}
                eventStartTime={formData.eventStartTime}
                eventLocation={formData.eventLocation}
                eventBackgroundImage={formData.eventBackgroundImage}
                isEvent={isEventNotice}
                layout="native"
              />
            </Box>
          ) : (
            <Box
              sx={{
                width: "100%",
                maxWidth: { xs: 300, md: 300 },
                mx: "auto",
                transform: { xs: "scale(0.82)", md: "scale(0.73)" },
                transformOrigin: "top center",
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  width: "100%",
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: theme.palette.event.border,
                  bgcolor: "background.paper",
                  boxShadow: `0 4px 14px ${alpha(theme.palette.common.black, 0.06)}`,
                  px: { xs: 1.25, sm: 1.5 },
                  py: { xs: 1.25, sm: 1.5 },
                }}
              >
                <NoticeCard
                  variant="detail"
                  title={formData.title}
                  body={formData.body}
                  createdAt={new Date().toISOString()}
                  sender="Preview"
                  type={previewCardType}
                  isUrgent={isUrgent}
                  targetSubcircle={formData.targetSubcircle}
                  attachments={previewAttachments}
                  schoolName={user?.schoolName || "Preview School"}
                  subcircles={subcircles}
                />
              </Paper>
            </Box>
          )}
          </Box>

        {showComposerAttachmentsGallery ? (
          <Box sx={{ mt: 1.5 }}>
            <NoticeComposerAttachmentsGallery files={composerGalleryFiles} />
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};
